/**
 * @Author: v_jppjpeng
 * @Date: 2022-11-17 15:54:04
 * @Explain: 相关接口、环境配置
 */
/** 环境 - 枚举 */
export enum EnvEnum {
  /** 开发环境 */
  DEV = 'dev',
  /** 测试环境 */
  TEST = 'test',
  /** 预发布环境 */
  STAGING = 'staging',
  /** 现网环境 */
  PROD = 'prod',
}
/** 域名配置 - 声明 */
type DomainConfigType = {
  /** 当前系统api */
  apiHost: string;
  /** playernetwork - 后台管理系统 */
  pntIntlGame: string;
  /** 中间件 - 单页应用 */
  middlewareHost: string;
  /** 文档链接 */
  docHost: string;
  /** woa登录 */
  woaLoginHost: string;
  /** 注册表单域名 */
  signUpFormHost: string;
  /** cdn静态资源 */
  staticResourceHost: string;
  /** 上报系统id */
  aegisId: string;
  /** 验证码配置 */
  reCaptureV2SiteKey: string;
  /** 验证码配置 */
  reCaptureV3SiteKey: string;
  /** 双因素 - 解密key */
  twoFaAesDecryptKey: string;
  /** nest api 前缀 */
  nestApiPrefile: string;
};

// 是否开发环境
export const isDev = process.env.NODE_ENV === 'development';

// 环境类型
export const envType = () => {
  let env = EnvEnum.PROD;
  try {
    if (isDev) {
      // 本地开发
      // env = EnvEnum.DEV;
      env = EnvEnum.TEST;
    } else {
      const origin = window && window?.location ? window.location.origin : '';
      const isDebug = origin.indexOf('console-debug') > -1;
      const isTest = origin.indexOf('console-test') > -1;
      const isStaging = origin.indexOf('console-staging') > -1;
      if (isDebug) {
        // 开发环境
        env = EnvEnum.DEV;
      } else if (isTest) {
        // 测试环境
        env = EnvEnum.TEST;
      } else if (isStaging) {
        // 预发布环境
        env = EnvEnum.STAGING;
      } else {
        // 现网环境
        env = EnvEnum.PROD;
      }
    }
  } catch (e) {
    if (isDev) {
      console.error('envType ==>', e);
    }
  }
  return env;
};
// cdn静态资源
const staticResourceHost =
  'https://cdn.smartlink.intlgame.com/console-smartlink';

// 域名配置 - 由envType方法切换环境
export const domainConfig: Record<EnvEnum, DomainConfigType> = {
  [EnvEnum.DEV]: {
    apiHost: 'https://console-debug.smartlink.intlgame.com',
    pntIntlGame: 'https://console-pre.playernetwork.intlgame.com',
    middlewareHost: 'https://sm-unsubscribe-test.intlgame.com',
    docHost: 'https://docs-test.smartlink.intlgame.com',
    woaLoginHost: 'https://console-test.smartlink.intlgame.com/saml/ioa_login',
    signUpFormHost: 'https://form-debug.smartlink.intlgame.com',
    staticResourceHost,
    aegisId: 'RrjXmuP49pWQlbO3Rj',
    reCaptureV2SiteKey: '6LeHN-0pAAAAAHl-kiSYVAZl6ZvVkq09DAxsKqeb',
    reCaptureV3SiteKey: '6LdVcRAqAAAAAEX78aXVfkpMTxWv1QlCCo9lu80u',
    twoFaAesDecryptKey: '53458fd7-6bbb-590d-dcdb-51e5216def5c',
    nestApiPrefile: '/capi/v2',
  },
  [EnvEnum.TEST]: {
    apiHost: 'https://api-test.smartlink.intlgame.com',
    pntIntlGame: 'https://console-test.playernetwork.intlgame.com',
    middlewareHost: 'https://sm-unsubscribe-test.intlgame.com',
    docHost: 'https://docs-test.smartlink.intlgame.com',
    woaLoginHost: 'https://console-test.smartlink.intlgame.com/saml/ioa_login',
    signUpFormHost: 'https://form-test.smartlink.intlgame.com',
    staticResourceHost,
    aegisId: 'RrjXmuP49pWQlbO3Rj',
    reCaptureV2SiteKey: '6LeHN-0pAAAAAHl-kiSYVAZl6ZvVkq09DAxsKqeb',
    reCaptureV3SiteKey: '6LdVcRAqAAAAAEX78aXVfkpMTxWv1QlCCo9lu80u',
    twoFaAesDecryptKey: '53458fd7-6bbb-590d-dcdb-51e5216def5c',
    nestApiPrefile: 'https://capi-test.smartlink.intlgame.com/n/api',
  },
  [EnvEnum.STAGING]: {
    apiHost: 'https://console-staging.smartlink.intlgame.com',
    pntIntlGame: 'https://console-test.playernetwork.intlgame.com',
    middlewareHost: 'https://sm-unsubscribe-staging.intlgame.com',
    docHost: 'https://docs-staging.smartlink.intlgame.com',
    woaLoginHost: 'https://console-test.smartlink.intlgame.com/saml/ioa_login',
    signUpFormHost: 'https://form-staging.smartlink.intlgame.com',
    staticResourceHost,
    aegisId: '3olwdiLXgpbjKpZeey',
    reCaptureV2SiteKey: ' 6Lcpb7EqAAAAAIYvxwCRW19aq9LA5ZFIk2ayvt4X',
    reCaptureV3SiteKey: '6Lefb7EqAAAAABW5qXowlrozyB1G9cX0e-Ph6osv',
    twoFaAesDecryptKey: '81fbfff5-ccd8-dc62-6ac4-ff202d17ea90',
    nestApiPrefile: 'https://capi.smartlink.intlgame.com/n/api',
  },
  [EnvEnum.PROD]: {
    apiHost: 'https://console.smartlink.intlgame.com',
    pntIntlGame: 'https://console.playernetwork.intlgame.com',
    middlewareHost: 'https://sm-unsubscribe.intlgame.com',
    docHost: 'https://docs.smartlink.intlgame.com',
    woaLoginHost: 'https://console.smartlink.intlgame.com/saml/ioa_login',
    signUpFormHost: 'https://form.smartlink.intlgame.com',
    staticResourceHost,
    aegisId: 'j42WvsL05RkwDze0WQ',
    reCaptureV2SiteKey: '6LcPNu0pAAAAAIab6LTDKgipCJFE7oMTkYQDk4Yh',
    reCaptureV3SiteKey: '6LevBjgqAAAAACWaiDXq9iSKfBlGanz2QFmVkimP',
    twoFaAesDecryptKey: '9147115d-166b-a5f0-c7fc-2b7d9b7912c4',
    nestApiPrefile: 'https://capi.smartlink.intlgame.com/n/api',
  },
};

/**
 * API配置
 * 本地开发 - 使用umi配置的proxy
 * 构建版本 - 会做反向代理
 */
if (isDev) {
  console.log('envType ==>', envType());
}
export const API = domainConfig[envType()];
